import React from "react";

import { Button, Label, Typography } from "clearsense-ui/components";
import { Input, Switch, Select } from "clearsense-ui/components/formik";

import NestedFields from "./NestedFields";

import { FIELD_TYPES } from "../../constants";
import { dataCy, snakeCaseToTitleCase } from "../../utils";

const Fields = ({ fields }) => {
  const getExamples = examples => {
    const isExamplePresent = examples?.length > 0;
    return isExamplePresent ? `Examples: ${examples.join(", ")}` : "";
  };

  const getDescription = (description, examples) => {
    const linkTagAndText = /<a [^>]+>(.*?)<\/a>/g.exec(description);
    if (linkTagAndText?.length > 0) {
      const link = description.match(/\bhttps?:\/\/\S+/gi);
      const [linkTag, linkText] = linkTagAndText;
      const [textBeforeTag, textAfterTag] = description.split(linkTag);
      return (
        <>
          <Typography style="small">
            {textBeforeTag}
            <Button
              href={link}
              style="text"
              target="_blank"
              rel="noopener noreferrer"
              className="px-0 text-xs"
              label={linkText}
            />
            {textAfterTag}
          </Typography>{" "}
        </>
      );
    }

    return `${description} ${getExamples(examples)}`;
  };

  const checkForDescription = (description, examples) =>
    description ? getDescription(description, examples) : "";

  const otherInputs = field => {
    switch (field.type) {
      case "boolean":
        return (
          <div key={field.key} className="space-y-2">
            <Switch
              name={field.key}
              label={field?.title ?? snakeCaseToTitleCase(field.key)}
              data-cy={`new-${dataCy(field.key)}-switch`}
            />
            <Label className="text-xs text-gray-500">
              {checkForDescription(field?.description, field?.examples)}
            </Label>
          </div>
        );
      case "object":
        return (
          <NestedFields
            field={field}
            checkForDescription={checkForDescription}
          />
        );
      case "array": {
        const defaultOption = field?.default && field?.default[0];
        return (
          <Select
            isMulti
            isCreateable
            key={field.key}
            name={field.key}
            label={`${field.title} (Creatable)`}
            data-cy={`new-${dataCy(field.key)}-multi-select`}
            helpText={checkForDescription(field?.description, field?.examples)}
            defaultOptions={
              defaultOption
                ? [{ label: defaultOption, value: defaultOption }]
                : []
            }
          />
        );
      }
      case "string":
        return (
          <Select
            key={field.key}
            name={field.key}
            label={field.title}
            data-cy={`new-${dataCy(field.key)}-select`}
            options={field?.enum?.map(option => ({
              label: option,
              value: option,
            }))}
            required={field?.required}
            helpText={checkForDescription(field?.description, field?.examples)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {fields.map(field => {
        const isInput =
          Object.keys(FIELD_TYPES).includes(field.type) && !field?.enum;
        return (
          <div key={field.key}>
            {isInput ? (
              <Input
                name={field.key}
                label={field?.title ?? snakeCaseToTitleCase(field.key)}
                type={FIELD_TYPES[field.type]}
                helpText={checkForDescription(
                  field?.description,
                  field?.examples
                )}
                required={field?.required}
                data-cy={`new-${dataCy(field.key)}-input`}
              />
            ) : (
              otherInputs(field)
            )}
          </div>
        );
      })}
    </>
  );
};

export default Fields;
